import { Button, FontIcon } from '@intigral/shared';
import styles from '@intigral/shared/styles/components/promoslider.module.sass';
import { playerTypes } from '@libs/constants';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GenericBitmovinPlayer } from '../../../../features/player';
import playerService from '../../../../helpers/bitmovinPlayer';
import { isPlayerAlive, safePlayerPlay } from '../../../../helpers/playerGuards';
import { useConfigurationSections } from '../../../../hooks/useConfiguration';
import { IStore } from '../../../../interfaces/StoreTypes';
import { setMuteState } from '../../../../store/reducers/playerSlice';

const PromoSlideTrailer = ({ item, active, onFinish, onStart, onDestroy, onError }) => {
    const dispatch = useDispatch();
    const isLiveStream = Boolean(item?.liveStream);
    // Using React Query instead of Redux for configuration
    const { promoBannerLiveStream } = useConfigurationSections();
    const isMuted = useSelector((state: IStore) => state.player.isMuted);
    const { clipDurationInSec = 10 } = promoBannerLiveStream || {};

    const [showMuteBtn, setShowMuteBtn] = useState(false);
    const playerRef = useRef(null);

    const handleMute = () => {
        dispatch(setMuteState(!isMuted));
        const player = playerService.getPlayer?.() ?? playerService.player;
        if (!player || !isPlayerAlive(player)) return;
        playerService.toggleMute();
    };

    // `touchStart`/`touchEnd` handlers are added in parent `PromoSlider` component to scroll slides on mobile device
    // Stoping this events propagation in order to disable slides scrolling when clicking mute/unmute button
    const handleTouch = (e) => e.stopPropagation();

    useEffect(() => {
        if (!active) return;
        setShowMuteBtn(true);
        onStart?.();
    }, [active, playerService.playerObj]);

    useEffect(() => {
        if (!active || !isLiveStream) return;

        const timer = setTimeout(() => {
            onFinish?.();
        }, clipDurationInSec * 1000);

        return () => clearTimeout(timer);
    }, [active, isLiveStream, clipDurationInSec, onFinish]);

    useEffect(() => {
        const handleScroll = () => {
            const container = playerRef.current;
            const player = playerService.getPlayer?.() ?? playerService.player;
            if (!container || !player || !isPlayerAlive(player)) return;

            const { top, bottom } = container.getBoundingClientRect();
            const height = (bottom - top) * 0.2;
            const scrollPosition = bottom - height;

            if (window.scrollY > scrollPosition) {
                player.pause();
            } else {
                safePlayerPlay(player);
            }
        };

        if (playerRef.current && active) {
            window.addEventListener('scroll', handleScroll);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [active]);

    useEffect(() => {
        if (!active) {
            setShowMuteBtn(false);
        }
    }, [active]);

    const source = item.media ? item?.media?.releases[0]?.plrelease$url : item?.liveStream?.tuningURLs?.liveURL ?? '';

    // Always use Trailer player type for hero promos (including live clips).
    // LiveTV enables the full Bitmovin UI/seekbar whose TimelineMarkersHandler 1s timer
    // is what leaked `player.isLive` errors when switching promotions.
    if (!active || !source) {
        return null;
    }

    return (
        <>
            {showMuteBtn && (
                <div className={styles['mute-holder']}>
                    <Button
                        className={styles['button-mute']}
                        onClick={handleMute}
                        onTouchStart={handleTouch}
                        onTouchEnd={handleTouch}
                        data-test-id="mute-button"
                    >
                        <FontIcon name={isMuted ? 'mute' : 'volume-1'} size="smaller" md="smaller"></FontIcon>
                    </Button>
                </div>
            )}
            <GenericBitmovinPlayer
                key={item?.Id ?? source}
                ref={playerRef}
                streamingUrl={source}
                playerType={playerTypes.Trailer}
                onFinish={onFinish}
                onDestroy={onDestroy}
                onError={onError}
                isMuted={isMuted}
                VODAsset={isLiveStream ? null : item}
                LinearAsset={isLiveStream ? item : null}
            />
        </>
    );
};

export default PromoSlideTrailer;
